<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import CheckMark from 'dashboard/components-next/checkbox/CheckMark.vue';
import {
  DropdownContainer,
  DropdownBody,
  DropdownSection,
  DropdownItem,
} from 'dashboard/components-next/dropdown-menu/base';
import { useDropdownContext } from 'dashboard/components-next/dropdown-menu/base/provider.js';

// CRM-01h: um filtro da barra do Kanban, no formato do HubSpot. Sem valor, mostra só o nome;
// com valor, fica em destaque, mostra o que foi escolhido ("Caixa: Bella +1") e ganha um ✕.
// UI-FILTROS-01 (§2.1): a mesma pílula para toda barra de filtros. Tudo o que entrou depois do
// Kanban é opcional, e sem essas props ela se comporta como antes.
const props = defineProps({
  label: { type: String, required: true },
  // [{ id, name }]
  options: { type: Array, default: () => [] },
  // Um valor só (ex.: "Na etapa: 7+ dias"); escolher outro troca, em vez de somar.
  single: { type: Boolean, default: false },
  // A partir de quantas opções aparece a busca da lista.
  searchFrom: { type: Number, default: 7 },
  // O resumo de quem não tem opções (texto, data): "Nome: contém ana".
  summary: { type: String, default: '' },
  // Quando vem, decide o destaque: "vazio" e "preenchido" ficam ativos sem valor.
  active: { type: Boolean, default: undefined },
  // Onde o vazio quer dizer "todos": "Caixa: Todas".
  allLabel: { type: String, default: '' },
  // Com false, o valor nunca fica vazio pela pílula (ordenação): sem ✕, e clicar no marcado
  // não desmarca.
  clearable: { type: Boolean, default: true },
  // Apagada: não abre, e o porquê aparece ao passar o mouse ou tocar.
  disabled: { type: Boolean, default: false },
  disabledHint: { type: String, default: '' },
  // Uma linha "Selecionar todos" no alto da lista (só na múltipla).
  selectAll: { type: Boolean, default: false },
  // Altura máxima e largura mínima da lista, em classes. Uma lista fixa sem busca (a ordenação,
  // 9 opções = 388 px) rolava por 4 px nos 384 do max-h-96, com barra no computador.
  listHeight: { type: String, default: 'max-h-96' },
  listWidth: { type: String, default: 'min-w-56' },
});

const emit = defineEmits(['clear']);

const selecionados = defineModel({ type: Array, default: () => [] });

const { t } = useI18n();
const busca = ref('');

const ativo = computed(() => props.active ?? selecionados.value.length > 0);

const resumo = computed(() => {
  if (props.summary) return props.summary;
  if (!selecionados.value.length) return '';
  const nomes = props.options
    .filter(opcao => selecionados.value.includes(opcao.id))
    .map(opcao => opcao.name);
  // Filtro vindo de um link antes das opções carregarem: mostra quantos, não um nome vazio.
  if (!nomes.length) return String(selecionados.value.length);
  const resto = selecionados.value.length - 1;
  return resto > 0 ? `${nomes[0]} +${resto}` : nomes[0];
});

// O que o gatilho diz além do nome: o escolhido, ou o "todos" de quem não escolheu nada.
const valorVisivel = computed(() =>
  ativo.value ? resumo.value : props.allLabel
);

const semAcento = texto =>
  String(texto || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();

const opcoesVisiveis = computed(() => {
  const termo = semAcento(busca.value.trim());
  if (!termo) return props.options;
  return props.options.filter(opcao => semAcento(opcao.name).includes(termo));
});

const marcada = id => selecionados.value.includes(id);

const alternar = id => {
  if (props.single) {
    if (!marcada(id)) selecionados.value = [id];
    else if (props.clearable) selecionados.value = [];
    return;
  }
  if (!marcada(id)) {
    selecionados.value = [...selecionados.value, id];
    return;
  }
  if (!props.clearable && selecionados.value.length === 1) return;
  selecionados.value = selecionados.value.filter(valor => valor !== id);
};

const todasMarcadas = computed(
  () => props.options.length > 0 && props.options.every(({ id }) => marcada(id))
);
const estadoDeTodas = computed(() => {
  if (todasMarcadas.value) return 'true';
  return selecionados.value.length ? 'mixed' : 'false';
});
const alternarTodas = () => {
  if (!todasMarcadas.value) {
    selecionados.value = props.options.map(({ id }) => id);
  } else if (props.clearable) {
    selecionados.value = [];
  }
};

const limpar = () => {
  selecionados.value = [];
  emit('clear');
};

// O porquê da pílula apagada vai num tooltip que abre também no clique: o `title` nativo não
// aparece no toque, e no celular a pílula ficava muda. Some ao sair dela ou perder o foco.
const dicaApagada = computed(() =>
  props.disabled && props.disabledHint
    ? {
        content: props.disabledHint,
        triggers: ['hover', 'focus', 'click'],
        hideTriggers: ['hover', 'focus'],
      }
    : null
);

const abrir = toggle => {
  if (props.disabled) return;
  busca.value = '';
  toggle();
};

// O #body (texto, número, data) precisa fechar a lista quando aplica. Quem fecha é o contexto
// do dropdown, que só existe dentro dele.
const ComFechar = (_, { slots }) =>
  slots.default?.({ close: useDropdownContext().closeMenu });
</script>

<template>
  <DropdownContainer>
    <template #trigger="{ toggle, isOpen }">
      <div
        class="flex items-center h-8 text-sm rounded-lg"
        :class="[
          ativo
            ? 'bg-pz-brand-tint text-pz-brand-on-tint'
            : 'outline outline-1 outline-n-weak text-n-slate-12',
          { 'hover:bg-n-alpha-1': !ativo && !disabled, 'opacity-50': disabled },
        ]"
      >
        <button
          v-tooltip.bottom="dicaApagada"
          type="button"
          class="flex items-center h-full gap-1 px-2.5 rounded-lg whitespace-nowrap"
          :class="{ 'cursor-not-allowed': disabled }"
          :aria-expanded="isOpen"
          :aria-disabled="disabled || undefined"
          :aria-label="
            valorVisivel
              ? t('FILTER_BAR.PILL_SUMMARY', { label, value: valorVisivel })
              : label
          "
          @click="abrir(toggle)"
        >
          <span :class="{ 'font-medium': ativo }">{{
            valorVisivel ? t('FILTER_BAR.PILL_LABEL', { label }) : label
          }}</span>
          <span
            v-if="valorVisivel"
            class="truncate max-w-40"
            :class="{ 'font-medium': ativo }"
          >
            {{ valorVisivel }}
          </span>
          <!-- A seta diz "abre uma lista". A pílula que não se zera (ordenação) está sempre ativa e
               não tem ✕: sem a seta, parecia uma etiqueta parada. -->
          <Icon
            v-if="!ativo || !clearable"
            icon="i-lucide-chevron-down"
            class="size-3.5"
            :class="{ 'text-n-slate-11': !ativo }"
          />
        </button>
        <button
          v-if="ativo && clearable && !disabled"
          type="button"
          class="grid h-full pe-2 place-content-center"
          :aria-label="t('FILTER_BAR.REMOVE', { name: label })"
          @click="limpar"
        >
          <Icon icon="i-lucide-x" class="size-3.5" />
        </button>
      </div>
    </template>
    <!-- No celular a lista abre fixa na largura da tela: ancorada no gatilho, uma pílula perto
         da borda direita jogaria a lista para fora. -->
    <DropdownBody
      class="z-50 mt-1 max-sm:fixed max-sm:inset-x-4 max-sm:min-w-0"
      :class="listWidth"
    >
      <div v-if="$slots.operator" class="px-2 pt-1 pb-2">
        <slot name="operator" />
      </div>
      <ComFechar v-if="$slots.body" v-slot="{ close }">
        <slot name="body" :close="close" />
      </ComFechar>
      <div
        v-if="!$slots.body && options.length >= searchFrom"
        class="px-2 pt-1 pb-2"
      >
        <input
          v-model="busca"
          type="search"
          class="w-full h-8 px-3 text-sm rounded-lg outline-none reset-base bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:ring-1 focus:ring-n-brand"
          :placeholder="t('FILTER_BAR.SEARCH_OPTIONS')"
          :aria-label="t('FILTER_BAR.SEARCH_OPTIONS')"
        />
      </div>
      <p
        v-if="!$slots.body && !opcoesVisiveis.length"
        class="px-3 py-2 text-sm text-n-slate-11"
      >
        {{ t('FILTER_BAR.NO_OPTIONS') }}
      </p>
      <!-- Coluna que não cresce com o nome mais longo: o nome encurta em vez de empurrar.
           Cada opção é um <button> (D2): o teclado chega nela, e Enter e Espaço a marcam. -->
      <DropdownSection
        v-else-if="!$slots.body"
        class="[&>ul]:grid-cols-1"
        :height="listHeight"
      >
        <DropdownItem
          v-if="selectAll && !single"
          preserve-open
          type="button"
          class="cursor-pointer focus-visible:bg-n-alpha-2"
          role="menuitemcheckbox"
          :aria-checked="estadoDeTodas"
          :click="alternarTodas"
        >
          <template #label>
            <CheckMark
              :checked="todasMarcadas"
              :indeterminate="estadoDeTodas === 'mixed'"
            />
            <span class="min-w-0 font-medium truncate text-n-slate-12">{{
              t('FILTER_BAR.SELECT_ALL')
            }}</span>
          </template>
        </DropdownItem>
        <DropdownItem
          v-for="opcao in opcoesVisiveis"
          :key="opcao.id"
          :preserve-open="!single"
          type="button"
          class="cursor-pointer focus-visible:bg-n-alpha-2"
          role="menuitemcheckbox"
          :aria-checked="marcada(opcao.id)"
          :click="() => alternar(opcao.id)"
        >
          <template #label>
            <CheckMark v-if="!single" :checked="marcada(opcao.id)" />
            <slot name="option" :option="opcao" :checked="marcada(opcao.id)">
              <span class="min-w-0 truncate text-n-slate-12">{{
                opcao.name
              }}</span>
            </slot>
            <Icon
              v-if="single && marcada(opcao.id)"
              icon="i-lucide-check"
              class="flex-shrink-0 ms-auto size-4 text-n-brand"
            />
          </template>
        </DropdownItem>
      </DropdownSection>
    </DropdownBody>
  </DropdownContainer>
</template>
